import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  BusinessRecordComplianceConfigurationDto,
  SaveBusinessRecordComplianceRuleRequestDto,
} from './organisationApiTypes';

const base = (businessRecordId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/compliance-rules`;

// The body is read even on an error status, because business refusals carry
// their operator-facing message in the JSON envelope. An unhandled server
// error is not JSON at all — ASP.NET returns a plain-text exception page —
// and parsing it used to throw first, hiding the status behind a JSON syntax
// error. Fall back to the status instead.
const parse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => null) as ApiResponse<T> | null;
  if (!response.ok) throw new Error(body?.message || `Request failed: ${response.status}`);
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable Compliance rules response.');
  return body.result;
};

const json = (method: 'PUT' | 'POST', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const getComplianceRulesConfiguration = async (
  businessRecordId: string,
): Promise<BusinessRecordComplianceConfigurationDto> =>
  parse(await authFetch(base(businessRecordId)));

export const saveComplianceRule = async (
  businessRecordId: string,
  ruleId: string,
  request: SaveBusinessRecordComplianceRuleRequestDto,
): Promise<BusinessRecordComplianceConfigurationDto> =>
  parse(await authFetch(`${base(businessRecordId)}/${encodeURIComponent(ruleId)}`, json('PUT', request)));

export const resetComplianceRulesToDefaults = async (
  businessRecordId: string,
): Promise<BusinessRecordComplianceConfigurationDto> =>
  parse(await authFetch(`${base(businessRecordId)}/reset`, json('POST', {})));
